import React, { useState, useRef, useEffect } from 'react';
import { BracketGame, WinnerSource, WinnerWithMetadata } from '../types/types';
import { GameBreakdown } from '../utils/pickBreakdownUtils';
import { useConfig } from '../context/ConfigContext';
import { Trophy, X, Pencil, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';

interface FinalFourCenterProps {
  games: BracketGame[];
  winnersWithMetadata: WinnerWithMetadata[];
  renderGame: (game: BracketGame) => React.ReactNode;
  renderChampionshipGame?: (game: BracketGame) => React.ReactNode;
  setGames: React.Dispatch<React.SetStateAction<BracketGame[]>>;
  onBracketChange: (updatedGames: BracketGame[]) => void;
  /** When provided, shows champion pick breakdown instead of "TBD" */
  championBreakdown?: GameBreakdown;
}

/**
 * Inline-editable label for round names in the center column.
 */
const EditableCenterLabel: React.FC<{
  label: string;
  configIndex: number;
  className?: string;
}> = ({ label, configIndex, className }) => {
  const { updateRoundName } = useConfig();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setEditValue(label); }, [label]);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (!editValue.trim()) {
      setEditValue(label);
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await updateRoundName(configIndex, editValue.trim());
    } catch {
      setEditValue(label);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <span className={cn("inline-flex items-center gap-0.5", className)} onClick={e => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') { setEditValue(label); setIsEditing(false); }
          }}
          disabled={isSaving}
          className="bg-transparent border-b border-foreground/30 outline-none text-[9px] uppercase tracking-wider font-medium w-20 text-center text-foreground"
        />
        <button onClick={handleSave} disabled={isSaving} className="text-success hover:text-success/80">
          <Check className="h-2.5 w-2.5" />
        </button>
        <button onClick={() => { setEditValue(label); setIsEditing(false); }} disabled={isSaving} className="text-destructive hover:text-destructive/80">
          <X className="h-2.5 w-2.5" />
        </button>
      </span>
    );
  }

  return (
    <span className={cn("inline-flex items-center gap-0.5 group cursor-pointer", className)} onClick={() => setIsEditing(true)}>
      {label}
      <Pencil className="h-2 w-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
    </span>
  );
};

/**
 * Renders the Final Four, Championship, and Champion in a compact
 * vertical stack for the center column of the desktop bracket view.
 */
const FinalFourCenter: React.FC<FinalFourCenterProps> = ({
  games,
  winnersWithMetadata,
  renderGame,
  renderChampionshipGame,
  setGames,
  onBracketChange,
  championBreakdown,
}) => {
  const { roundConfigs } = useConfig();
  const finalFourTitle = roundConfigs[3]?.name || 'Final Four';
  const championshipTitle = roundConfigs[4]?.name || 'Championship Game';
  const championTitle = roundConfigs[5]?.name || 'Champion';

  const finalFourGames = games.filter(g => g.round === 4).sort((a, b) => a.id - b.id);
  const championshipGame = games.find(g => g.round === 5);

  if (!championshipGame || finalFourGames.length < 2) return null;

  const champion = championshipGame.winner;
  const winnerMetadata = championshipGame.id < winnersWithMetadata.length
    ? winnersWithMetadata[championshipGame.id]
    : null;
  const isOfficialWinner = winnerMetadata &&
    winnerMetadata.team === champion &&
    winnerMetadata.source === WinnerSource.OFFICIAL;

  const handleChampionReset = () => {
    if (!champion || isOfficialWinner) return;
    const updatedGames = [...games];
    const champGame = updatedGames.find(g => g.id === championshipGame.id);
    if (champGame) {
      champGame.winner = undefined;
      setGames(updatedGames);
      onBracketChange(updatedGames);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full px-1">
      {/* Final Four label centered across both games */}
      <div className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider mb-1 pointer-events-auto">
        <EditableCenterLabel label={finalFourTitle} configIndex={3} />
      </div>

      {/* FF games side by side: FF60 on left, FF61 on right */}
      <div className="flex items-end gap-6 pointer-events-auto">
        <div className="min-w-[160px]">
          {renderGame(finalFourGames[0])}
        </div>
        <div className="min-w-[160px]">
          {renderGame(finalFourGames[1])}
        </div>
      </div>

      {/* Connector */}
      <div className="h-2 border-l-2 border-border/40" />

      {/* Championship game — centered below both FF games */}
      <div className="flex flex-col items-center gap-0.5 pointer-events-auto">
        <div className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">
          <EditableCenterLabel label={championshipTitle} configIndex={4} />
        </div>
        <div className="min-w-[200px]">
          {(renderChampionshipGame || renderGame)(championshipGame)}
        </div>
      </div>

      {/* Champion display — hidden in breakdown mode */}
      {!championBreakdown && (
        <div className={cn(
          "rounded-md border px-2 py-1.5 text-center mt-1 min-w-[200px] pointer-events-auto",
          champion ? "border-primary bg-primary/5" : "border-border"
        )}>
          <div className="flex items-center justify-center gap-1 text-[10px] font-bold text-primary">
            <Trophy className="h-3 w-3" />
            <EditableCenterLabel label={championTitle} configIndex={5} />
            <Trophy className="h-3 w-3" />
          </div>
          {champion ? (
            <div className="text-[22px] font-bold text-foreground flex items-center justify-center gap-1 mt-0.5">
              {champion}
              {isOfficialWinner && (
                <Badge variant="teal" className="text-[9px] px-1 py-0">Official</Badge>
              )}
              {!isOfficialWinner && (
                <button
                  className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-full bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/40 transition-colors"
                  onClick={handleChampionReset}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          ) : (
            <div className="text-[10px] text-muted-foreground mt-0.5">TBD</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinalFourCenter;
